// Selected "providers" (schemas) for the cross-schema list views. Persists the
// selection per-user in localStorage and keeps the list pages in sync within a
// session. The home schema is always included and cannot be unchecked. This
// never mutates the app-wide active schema; it is purely a view filter + a
// per-row _schema tag source.
//
// The offered set is the home schema's CLINIC GROUP (schema_settings."Group").
// Each schema is one doctor, so the group is the practice — the people whose
// charts a user legitimately works across. It replaced the old source, the
// comma list in public.users.schema, which was a per-user access list and
// therefore both too narrow (a doctor listing only their own schema saw no
// providers at all, defeating the feature) and unrelated to the clinic.
//
// Falls back to users.schema whenever the home schema is ungrouped or
// schema_settings can't be read, so a database without groups configured keeps
// exactly its previous behaviour.
import { useCallback, useEffect, useState } from 'react';
import { getSupabase, supabasePublic, supabaseSchema } from '../components/supabaseClient';
import { groupsFromRows, rowsToSchemaGroups } from '../utils/schemaGroups';
import { isImpersonating } from '../utils/impersonation';

const keyFor = (userId) => `vmai:selected-providers:${userId}`;
const EVT = 'vmai:providers-changed';

// The users.schema + schema_settings reads, once per signed-in user and shared
// by every instance (Schedules, SchedulesTable, ProvidersDropdown, the
// calendar, AdminPage, the fax inbox, ...). Each instance used to make both
// at mount, so a page paid 6-8 GETs, and SchedulesTable fetched the day twice:
// with the default selection, then again once its own copy resolved. A load
// that hit an error is handed to its callers but not kept, so the next mount
// tries again, as every mount used to.
const loaderByUid = new Map(); // uid -> { promise, value }
// The user whose resolved context a new instance starts from.
let lastUid = null;

function readStoredSelection(uid) {
  try { return JSON.parse(localStorage.getItem(keyFor(uid)) || '[]'); } catch (_) { return []; }
}

function forgetContext(uid, entry) {
  if (loaderByUid.get(uid) === entry) loaderByUid.delete(uid);
}

function forgetAllContexts() {
  loaderByUid.clear();
  lastUid = null;
}

function loadProviderContext(supabase, uid) {
  const hit = loaderByUid.get(uid);
  if (hit) return hit.promise;
  const entry = { promise: null, value: null };
  entry.promise = (async () => {
    let complete = true;
    const { data, error } = await supabase
      .schema('public').from('users').select('schema').eq('id', uid).single();
    if (error) {
      complete = false;
      console.warn('[useSelectedProviders] users.schema fetch failed:', error.message);
    }
    const parts = String(data?.schema || supabaseSchema)
      .split(',').map((s) => s.trim()).filter(Boolean);
    const accessible = parts.length ? parts : [supabaseSchema];
    const home = accessible[0];

    // Prefer the home schema's clinic group; fall back to users.schema.
    let all = accessible;
    try {
      const { data: rows, error: sErr } = await supabasePublic()
        .from('schema_settings').select('*');
      if (sErr) throw sErr;
      const group = groupSchemasFor(rowsToSchemaGroups(rows), home);
      if (group && group.length) all = group;
    } catch (e) {
      complete = false;
      console.warn('[useSelectedProviders] schema group lookup failed:', e?.message);
    }

    const value = { uid, all, accessible, home };
    if (complete) {
      entry.value = value;
      lastUid = uid;
    } else {
      forgetContext(uid, entry);
    }
    return value;
  })();
  entry.promise.catch(() => forgetContext(uid, entry));
  loaderByUid.set(uid, entry);
  return entry.promise;
}

// A context resolved earlier in this page load, with the stored selection
// applied, so a later mount's first render already carries the real selection.
function startingContext() {
  const ctx = lastUid ? loaderByUid.get(lastUid)?.value : null;
  if (!ctx) return null;
  return { ...ctx, selected: reconcileSelection(readStoredSelection(ctx.uid), ctx.all, ctx.home) };
}

/** Test hook: forget every loaded context. */
export function _resetProvidersCacheForTests() {
  forgetAllContexts();
}

/**
 * Schemas of the clinic `home` belongs to, home first, or null when it is
 * ungrouped. Exported for the tests and for callers that want the group
 * regardless of what is currently ticked.
 */
export function groupSchemasFor(rows, home) {
  const group = groupsFromRows(rows).find((g) =>
    g.schemas.some((s) => s.toLowerCase() === String(home || '').toLowerCase())
  );
  if (!group) return null;
  // Home first so it reads as "mine, then the rest of the practice"; the others
  // keep groupsFromRows' alphabetical order.
  return [home, ...group.schemas.filter((s) => s !== home)];
}

/**
 * The new value for `public.users.schema` after granting access to `wanted`, or
 * null when nothing changes.
 *
 * The dropdown offers the whole clinic group, but cross-schema opens are gated
 * on users.schema (see isSchemaAllowed in pages/medstudent/chartSource.js), so
 * a schema that is offered but missing from that column looks selectable and is
 * then refused. Ticking one is therefore also the grant.
 *
 * Order is load-bearing: the FIRST entry is the home schema — both this hook's
 * loader and SideNav take parts[0] — so grants are APPENDED and the existing
 * order is never rewritten.
 *
 * `allowed` bounds it, and is not optional in spirit: this writes the user's own
 * row with the user's own key, so without that bound it would be self-service
 * access to any schema in the database. Only the clinic group gets through.
 */
export function mergeSchemaAccess(currentCsv, wanted, allowed) {
  const current = String(currentCsv || '').split(',').map((s) => s.trim()).filter(Boolean);
  const have = new Set(current.map((s) => s.toLowerCase()));
  const permitted = new Set((allowed || []).map((s) => String(s || '').trim().toLowerCase()));
  const additions = [];
  for (const raw of (wanted || [])) {
    const name = String(raw || '').trim();
    const key = name.toLowerCase();
    if (!name || have.has(key) || !permitted.has(key)) continue;
    have.add(key);
    additions.push(name);
  }
  return additions.length ? [...current, ...additions].join(',') : null;
}

export function reconcileSelection(stored, allSchemas, homeSchema) {
  const valid = (Array.isArray(stored) ? stored : []).filter((s) => allSchemas.includes(s));
  return Array.from(new Set([homeSchema, ...valid]));
}

export function useSelectedProviders() {
  const [start] = useState(startingContext);
  const [allSchemas, setAllSchemas] = useState(start ? start.all : [supabaseSchema]);
  const [homeSchema, setHomeSchema] = useState(start ? start.home : supabaseSchema);
  const [userId, setUserId] = useState(start ? start.uid : null);
  const [selected, setSelected] = useState(start ? start.selected : [supabaseSchema]);
  // What users.schema currently grants. Distinct from `allSchemas`, which is
  // the whole clinic group and is usually WIDER — the gap is exactly what
  // grantAccess closes when a provider is ticked.
  const [accessible, setAccessible] = useState(start ? start.accessible : [supabaseSchema]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const { data: { session } = {} } = await supabase.auth.getSession();
        const uid = session?.user?.id;
        if (start && start.uid !== uid) {
          // The session changed hands since that context loaded (another tab
          // signed out or in): drop it, and never let its selection drive a
          // fetch or its user id take a grant.
          forgetAllContexts();
          if (cancelled) return;
          setAllSchemas([supabaseSchema]);
          setAccessible([supabaseSchema]);
          setHomeSchema(supabaseSchema);
          setUserId(null);
          setSelected([supabaseSchema]);
        }
        if (!uid) return;
        // Started from this user's context: nothing left to load.
        if (start && start.uid === uid) return;
        const { all, accessible, home } = await loadProviderContext(supabase, uid);
        if (cancelled) return;
        setAllSchemas(all);
        setAccessible(accessible);
        setHomeSchema(home);
        setUserId(uid);
        setSelected(reconcileSelection(readStoredSelection(uid), all, home));
      } catch (_) { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, [start]);

  useEffect(() => {
    if (!userId) return undefined;
    const onEvt = (e) => {
      if (e?.detail?.userId && e.detail.userId !== userId) return;
      let stored = [];
      try { stored = JSON.parse(localStorage.getItem(keyFor(userId)) || '[]'); } catch (_) {}
      setSelected(reconcileSelection(stored, allSchemas, homeSchema));
    };
    window.addEventListener(EVT, onEvt);
    return () => window.removeEventListener(EVT, onEvt);
  }, [userId, homeSchema, allSchemas]);

  // Append to public.users.schema so a ticked provider is actually openable.
  // Bounded to the clinic group by mergeSchemaAccess; silent on failure because
  // the view filter still works without it — the user just can't open a chart
  // in that schema, which is the pre-existing behaviour.
  const grantAccess = useCallback(async (wanted) => {
    if (!userId) return;
    // Never widen someone else's account from a view-as session. The admin is
    // exploring; the grant would persist on the target user after they leave.
    try { if (isImpersonating()) return; } catch (_) { /* treat as not impersonating */ }
    const pending = (wanted || []).filter(
      (s) => !accessible.some((a) => a.toLowerCase() === String(s || '').trim().toLowerCase())
    );
    if (!pending.length) return;
    try {
      const supabase = await getSupabase();
      // Re-read rather than trusting the copy loaded at mount: another tab may
      // have granted something since, and writing a stale list would revoke it.
      const { data, error } = await supabase
        .schema('public').from('users').select('schema').eq('id', userId).single();
      if (error) throw error;
      const next = mergeSchemaAccess(data?.schema, pending, allSchemas);
      if (!next) return;
      const { error: upErr } = await supabase
        .schema('public').from('users').update({ schema: next }).eq('id', userId);
      if (upErr) throw upErr;
      const granted = next.split(',').map((s) => s.trim()).filter(Boolean);
      setAccessible(granted);
      // Later mounts start from the shared context; keep its grant current.
      const entry = loaderByUid.get(userId);
      if (entry?.value) entry.value = { ...entry.value, accessible: granted };
    } catch (e) {
      console.warn('[useSelectedProviders] could not grant schema access:', e?.message || e);
    }
  }, [userId, accessible, allSchemas]);

  const toggle = useCallback((schema) => {
    if (schema === homeSchema) return;
    // Ticking a provider is also the grant — see mergeSchemaAccess.
    if (!selected.includes(schema)) grantAccess([schema]);
    setSelected((prev) => {
      const next = prev.includes(schema) ? prev.filter((s) => s !== schema) : [...prev, schema];
      const uniq = reconcileSelection(next, allSchemas, homeSchema);
      if (userId) {
        try { localStorage.setItem(keyFor(userId), JSON.stringify(uniq)); } catch (_) {}
        window.dispatchEvent(new CustomEvent(EVT, { detail: { userId } }));
      }
      return uniq;
    });
  }, [homeSchema, allSchemas, userId, selected, grantAccess]);

  // Tick every schema in the group. Used by /search, which is expected to span
  // the whole practice on entry rather than whatever was last left ticked.
  //
  // Persists like `toggle` does, so the change is visible on Schedules and
  // Patients too — the selection is deliberately one shared, sticky preference,
  // and a search-only override would make those pages disagree with it.
  const selectAll = useCallback(() => {
    grantAccess(allSchemas);
    setSelected((prev) => {
      const next = reconcileSelection(allSchemas, allSchemas, homeSchema);
      // Bail when nothing changes: writing anyway would fire the sync event on
      // every mount and re-render every listening page for no reason.
      if (prev.length === next.length && next.every((s) => prev.includes(s))) {
        return prev;
      }
      if (userId) {
        try { localStorage.setItem(keyFor(userId), JSON.stringify(next)); } catch (_) {}
        window.dispatchEvent(new CustomEvent(EVT, { detail: { userId } }));
      }
      return next;
    });
  }, [allSchemas, homeSchema, userId, grantAccess]);

  const isSelected = useCallback((schema) => selected.includes(schema), [selected]);

  return { allSchemas, homeSchema, accessible, selected, toggle, selectAll, isSelected };
}
